/**
 * useLiveChunks — IDB store of per-chunk recorder output for in-session
 * QC playback during a live recording.
 *
 * Background:
 *   useMatchRecorder previously tried to mirror the live recording to
 *   IDB as a single concatenated blob via saveLocalMatchFile. That
 *   approach works for file-mode uploads (the file is one valid MP4)
 *   but FAILS for live recording — Chrome's MediaRecorder emits each
 *   chunk as a self-contained MP4 (its own ftyp + moov pointing at its
 *   own mdat). Naive concatenation only plays the first chunk; the
 *   browser ignores subsequent ftyp/moov boxes.
 *
 * This store keeps each chunk as a separate IDB row, indexed by
 * (match_id, part_number). Each row also records the chunk's start
 * offset on the master timeline (every chunk is CHUNK_MS long, so
 * part N starts at (N-1) * CHUNK_MS). At QC playback time, the
 * BallVideoPlayer picks the ONE chunk that covers the ball's
 * video_start_ms and plays it standalone with a seek into the chunk-
 * local timeline.
 *
 * Memory: each chunk is ~11 MB at 1.5 Mbps × 60 s. A 90-minute match
 * keeps ~1 GB total in IDB. We evict the oldest beyond MAX_KEPT to
 * protect IDB quota on long matches.
 */

const IDB_DB    = "zcd_live_chunks_v1";
const IDB_STORE = "chunks";
const MAX_KEPT  = 60;      // ~60 min of clip-replayable history

function idbOpen() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(IDB_DB, 1);
    r.onupgradeneeded = () => {
      const store = r.result.createObjectStore(IDB_STORE, { keyPath: "key" });
      store.createIndex("match_id", "match_id", { unique: false });
    };
    r.onsuccess = () => res(r.result);
    r.onerror   = () => rej(r.error);
  });
}

const _key = (matchId, partNumber) => `${matchId}::${partNumber}`;


export async function saveLiveChunk({ matchId, partNumber, chunkMs, blob }) {
  if (!matchId || !partNumber || !blob) return;
  const db = await idbOpen();
  const tx = db.transaction(IDB_STORE, "readwrite");
  tx.objectStore(IDB_STORE).put({
    key:           _key(matchId, partNumber),
    match_id:      matchId,
    part_number:   partNumber,
    chunk_ms:      chunkMs,                 // chunk duration in ms (60_000 in our config)
    start_offset_ms: (partNumber - 1) * chunkMs,
    end_offset_ms: partNumber * chunkMs,
    blob,
    mime_type:     blob.type || "video/mp4",
    saved_at:      Date.now(),
  });
  return new Promise((r) => { tx.oncomplete = r; });
}


export async function listLiveChunks(matchId) {
  if (!matchId) return [];
  const db = await idbOpen();
  return new Promise((res) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const out = [];
    tx.objectStore(IDB_STORE)
      .index("match_id")
      .openCursor(IDBKeyRange.only(matchId)).onsuccess = (e) => {
        const c = e.target.result;
        if (!c) {
          out.sort((a, b) => a.part_number - b.part_number);
          return res(out);
        }
        out.push(c.value);
        c.continue();
      };
  });
}


// A real H.264 chunk at 1.5 Mbps × 60 s is ~11 MB. Anything below
// 200 KB is almost certainly an init-segment-only stub from a
// recorder that wasn't capturing frames (the legacy "1229-byte
// master" bug class). Treat as not-present so the SPA falls through
// to "Processing" instead of rendering a player that just hangs on
// "0:00 duration".
const MIN_PLAYABLE_BYTES = 200_000;

/**
 * Return every chunk that overlaps the [startMs, endMs] master-timeline
 * window, in chronological order. Includes the first chunk (chunks
 * 2..N are not independently playable without it — see MSE comment in
 * MSELiveClipPlayer). The first chunk carries the codec init segment
 * (ftyp + moov); subsequent chunks are media-segment only.
 *
 * Returns [] if no real chunks (all stubs / no overlap).
 */
export async function getChunksForRange(matchId, startMs, endMs) {
  if (!matchId || startMs == null || endMs == null) return [];
  const all = await listLiveChunks(matchId);
  // Keep only chunks that intersect the requested window AND are big
  // enough to contain real frames.
  const live = all.filter(
    (row) => (row.blob?.size || 0) >= MIN_PLAYABLE_BYTES,
  );
  if (live.length === 0) return [];

  // MSE needs the init segment (first emitted chunk) appended FIRST,
  // followed by media segments in order. If the requested clip lives
  // entirely in chunks 3-4, we still need chunks 1-2 prepended for the
  // browser to decode them — append everything from part 1 through
  // the last chunk that overlaps endMs.
  const lastOverlapIdx = live.reduce((acc, row, idx) => {
    if (row.start_offset_ms < endMs && row.end_offset_ms > startMs) {
      return Math.max(acc, idx);
    }
    return acc;
  }, -1);
  if (lastOverlapIdx < 0) return [];
  return live.slice(0, lastOverlapIdx + 1);
}


/**
 * Find the single chunk that covers ms. Returns null if no chunk overlaps
 * OR if the chunk that would have matched is too small to be a real
 * video segment (i.e. an init-only stub from a busted recorder run).
 */
export async function findChunkForMs(matchId, ms) {
  const all = await listLiveChunks(matchId);
  for (const row of all) {
    if (ms >= row.start_offset_ms && ms < row.end_offset_ms) {
      const sz = row.blob?.size || 0;
      if (sz < MIN_PLAYABLE_BYTES) {
        console.warn(
          "[live-chunks] chunk for ms=%d is %d bytes — too small to be a "
          + "real video segment. Skipping local playback (likely a stub "
          + "from a recorder run that didn't capture frames).",
          ms, sz,
        );
        return null;
      }
      console.info(
        "[live-chunks] chunk hit for ms=%d → part=%d size=%d bytes (chunk window %dms-%dms)",
        ms, row.part_number, sz, row.start_offset_ms, row.end_offset_ms,
      );
      return row;
    }
  }
  console.info("[live-chunks] no chunk covers ms=%d (total chunks=%d)", ms, all.length);
  return null;
}


export async function clearLiveChunks(matchId) {
  if (!matchId) return;
  const db = await idbOpen();
  const tx = db.transaction(IDB_STORE, "readwrite");
  tx.objectStore(IDB_STORE)
    .index("match_id")
    .openCursor(IDBKeyRange.only(matchId)).onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return;
      c.delete();
      c.continue();
    };
  return new Promise((r) => { tx.oncomplete = r; });
}

/**
 * Wipe the entire live-chunks IDB store. Exposed on window in dev
 * for DevTools-driven recovery — e.g. when stale chunks from a
 * previous schema/version are stuck and need a clean slate.
 *
 *     window.zcdResetLiveChunks()
 */
export async function resetAllLiveChunks() {
  return new Promise((res, rej) => {
    const r = indexedDB.deleteDatabase(IDB_DB);
    r.onsuccess = () => res(true);
    r.onerror   = () => rej(r.error);
    r.onblocked = () => console.warn("zcd_live_chunks_v1 delete blocked — close other tabs holding the DB and retry");
  });
}

if (typeof window !== "undefined") {
  window.zcdResetLiveChunks = resetAllLiveChunks;
}


/**
 * Evict the oldest chunks for matchId until <=MAX_KEPT remain. Called
 * after each save to bound IDB usage on long sessions.
 */
export async function trimOldestChunks(matchId) {
  const all = await listLiveChunks(matchId);
  if (all.length <= MAX_KEPT) return;
  const toDelete = all.slice(0, all.length - MAX_KEPT);
  const db = await idbOpen();
  const tx = db.transaction(IDB_STORE, "readwrite");
  for (const row of toDelete) {
    tx.objectStore(IDB_STORE).delete(row.key);
  }
  return new Promise((r) => { tx.oncomplete = r; });
}
